import { routergeraet, routergeraettyp, wartungsintervall } from '../../../utils/apiTypes';
import {
  RoutergeraetState,
  Zeitsynchronisationsart
} from "./routergeraetTypes";
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { getWithAuth, postJsonWithAuth, putWithAuth } from "../../../utils/api";
import type { RootState, AppDispatch } from "../../../app/store";
import { logError } from "../../../utils/utils";
import { openSnackbar } from "../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../utils/errorHandling';


export const routergeraetDummy: routergeraet = {
  id: 0,
  seriennummer: "",
  herstellungsjahr: new Date().getFullYear(),
  firmware: "",
  macadresse: null,
  kommentar: null,
  inbetriebnahmedatum: null,
  wartungsintervall: null,
  zeitsynchronisationsart: null,
  typ: -1,
  ipadresse: null,
  subnetzmaske: null,
  zugeordnet: false,
  jemalsZugeordnet: false
};

const initialState: RoutergeraetState = {
  editEntry: false,
  newEntry: false,
  routergeraeteListe: [],
  altesRoutergeraet: routergeraetDummy,
  aktuellesRoutergeraet: routergeraetDummy,
  zsList: [],
  routergeraetetypList: [],
  editDirty: 0,
  viewMode: false,
  error: [],
  wartungsintervalle: [],
  listLoading: false,
  routergeraettypenLoading: false,
  zeitsynchroLoading: false,
  wartungsintervallLoading: false,
  sorting: [
    {
      field: 'seriennummer',
      sort: 'asc',
    },
  ]
};


const routergeraetSlice = createSlice({
  name: "routergeraet",
  initialState,
  reducers: {
    updateRoutergeraeteListe: (state, action: PayloadAction<routergeraet>) => {
      const updateIndex = state.routergeraeteListe.findIndex(
        (x) => x.id == action.payload.id
      );
      const firstPart = state.routergeraeteListe.slice(0, updateIndex);
      let lastPart = Array<routergeraet>();
      if (updateIndex + 1 < state.routergeraeteListe.length)
        lastPart = state.routergeraeteListe.slice(updateIndex + 1);
      state.routergeraeteListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.seriennummer.localeCompare(b.seriennummer));
    },
    addAktuellesRoutergraetToRoutergeraeteListe: (state) => {
      const routergeraete = state.routergeraeteListe;
      routergeraete.push(state.aktuellesRoutergeraet);
      const sortedRoutergeraete = [...routergeraete].sort((a, b) => a.seriennummer.localeCompare(b.seriennummer));
      state.routergeraeteListe = sortedRoutergeraete;
    },
    removeFromRoutergeraeteListe: (state, action: PayloadAction<routergeraet>) => {
      const updateIndex = state.routergeraeteListe.findIndex(
        (x) => x.id == action.payload.id
      );
      const firstPart = state.routergeraeteListe.slice(0, updateIndex);
      let lastPart = Array<routergeraet>();
      if (updateIndex + 1 < state.routergeraeteListe.length)
        lastPart = state.routergeraeteListe.slice(updateIndex + 1);
      state.routergeraeteListe = [...firstPart, ...lastPart];
    },
    setRoutergeraeteListe: (state, action: PayloadAction<Array<routergeraet>>) => {
      state.routergeraeteListe = action.payload;
    },
    setAktuellesRoutergeraet: (state, action: PayloadAction<routergeraet>) => {
      state.aktuellesRoutergeraet = action.payload;
      state.altesRoutergeraet = action.payload;
    },
    setIdAktuellesRoutergeraet: (state, action: PayloadAction<number>) => {
      state.aktuellesRoutergeraet.id = action.payload;
    },
    setAltesRoutergeraet: (state, action: PayloadAction<routergeraet>) => {
      state.altesRoutergeraet = action.payload;
    },
    resetAktuellesRoutergeraet: (state) => {
      state.aktuellesRoutergeraet = state.altesRoutergeraet;
    },
    setZSs: (state, action: PayloadAction<Array<Zeitsynchronisationsart>>) => {
      state.zsList = action.payload;
    },
    setZS: (state, action: PayloadAction<string | null>) => {
      state.aktuellesRoutergeraet.zeitsynchronisationsart = action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.zeitsynchronisationsart ==
          state.altesRoutergeraet.zeitsynchronisationsart
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setRoutergeraettypen: (
      state,
      action: PayloadAction<Array<routergeraettyp>>
    ) => {
      state.routergeraetetypList = action.payload;
    },
    setRoutergeraettyp: (state, action: PayloadAction<number>) => {
      state.aktuellesRoutergeraet.typ = action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.typ == state.altesRoutergeraet.typ
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setSeriennummer: (state, action: PayloadAction<string>) => {
      state.aktuellesRoutergeraet.seriennummer = action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.seriennummer ==
          state.altesRoutergeraet.seriennummer
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setHerstellungsjahr: (state, action: PayloadAction<number>) => {
      state.aktuellesRoutergeraet.herstellungsjahr = action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.herstellungsjahr ==
          state.altesRoutergeraet.herstellungsjahr
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setFirmware: (state, action: PayloadAction<string>) => {
      state.aktuellesRoutergeraet.firmware = action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.firmware == state.altesRoutergeraet.firmware
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setMACAdresse: (state, action: PayloadAction<string>) => {
      state.aktuellesRoutergeraet.macadresse = action.payload == "" ? null : action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.macadresse == state.altesRoutergeraet.macadresse
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setIp: (state, action: PayloadAction<string>) => {
      state.aktuellesRoutergeraet.ipadresse = action.payload == "" ? null : action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.ipadresse == state.altesRoutergeraet.ipadresse
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setSubnetzmaske: (state, action: PayloadAction<string>) => {
      state.aktuellesRoutergeraet.subnetzmaske = action.payload == "" ? null : action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.subnetzmaske == state.altesRoutergeraet.subnetzmaske
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuellesRoutergeraet.kommentar = action.payload == "" ? null : action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.kommentar == state.altesRoutergeraet.kommentar
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setInbetriebnahmedatum: (state, action: PayloadAction<Date | null>) => {
      if (action.payload != null) {
        state.aktuellesRoutergeraet.inbetriebnahmedatum = new Date(
          action.payload.getTime() - action.payload.getTimezoneOffset() * 60000
        ).toISOString();

      }
      else {
        state.aktuellesRoutergeraet.inbetriebnahmedatum = null;
      }
      state.editDirty =
        state.aktuellesRoutergeraet.inbetriebnahmedatum ==
          state.altesRoutergeraet.inbetriebnahmedatum
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setWartungsintervall: (state, action: PayloadAction<string | null>) => {
      state.aktuellesRoutergeraet.wartungsintervall = action.payload;
      state.editDirty =
        state.aktuellesRoutergeraet.wartungsintervall ==
          state.altesRoutergeraet.wartungsintervall
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setWartungsintervalleList: (state, action: PayloadAction<Array<wartungsintervall>>) => {
      state.wartungsintervalle = action.payload;
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = 0;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setRoutergeraettypenLoading: (state, action: PayloadAction<boolean>) => {
      state.routergeraettypenLoading = action.payload;
    },
    setZeitsynchroLoading: (state, action: PayloadAction<boolean>) => {
      state.zeitsynchroLoading = action.payload;
    },
    setWartungsintervallLoading: (state, action: PayloadAction<boolean>) => {
      state.wartungsintervallLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  },
});

//Selectors
//======================================================================
export const selectRoutergeraeteListe = (state: RootState): routergeraet[] =>
  state.routergeraet.routergeraeteListe;
export const selectAktuellesRoutergeraet = (state: RootState): routergeraet =>
  state.routergeraet.aktuellesRoutergeraet;
export const selectZSs = (state: RootState): Zeitsynchronisationsart[] => state.routergeraet.zsList;
export const selectRoutergeraettypen = (state: RootState): routergeraettyp[] =>
  state.routergeraet.routergeraetetypList;
export const selectEditEntry = (state: RootState): boolean => state.routergeraet.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.routergeraet.newEntry;
export const selectEditDirty = (state: RootState): number => state.routergeraet.editDirty;
export const selectViewMode = (state: RootState): boolean => state.routergeraet.viewMode;
export const selectError = (state: RootState): boolean => state.routergeraet.error.length > 0;
export const selectWartungsintervalleList = (state: RootState): Array<wartungsintervall> => state.routergeraet.wartungsintervalle;
export const selectListLoading = (state: RootState): boolean => state.routergeraet.listLoading;
export const selectRoutergeraettypenLoading = (state: RootState): boolean => state.routergeraet.routergeraettypenLoading;
export const selectZeitsynchroLoading = (state: RootState): boolean => state.routergeraet.zeitsynchroLoading;
export const selectWartungsintervallLoading = (state: RootState): boolean => state.routergeraet.wartungsintervallLoading;
export const selectSorting = (state: RootState): GridSortModel => state.routergeraet.sorting;

//Actions
//=====================================================================
export const getRoutergeraeteListe = (idString: string | undefined) => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth("ROUTER", {})
    .then((response: Array<routergeraet>) => {
      dispatch(setRoutergeraeteListe(response));
      if (idString) {
          const id = Number(idString);
          const entry = response.find(x => x.id == id);
          if (entry) dispatch(updateAktuellesRoutergeraet(entry));
      }
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const getWartungsintervalle = () => (dispatch: AppDispatch): void => {
  dispatch(setWartungsintervallLoading(true));
  getWithAuth("WARTUNGSINTERVALL", {})
    .then((response) => {
      dispatch(setWartungsintervalleList(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setWartungsintervallLoading(false)));
};

export const getZSs = () => (dispatch: AppDispatch): void => {
  dispatch(setZeitsynchroLoading(true));
  getWithAuth("ZEITSYNCHRONISATIONSART", {})
    .then((response) => {
      dispatch(setZSs(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setZeitsynchroLoading(false)));
};

export const getRoutergeraettypen = () => (dispatch: AppDispatch): void => {
  dispatch(setRoutergeraettypenLoading(true));
  getWithAuth("ROUTERTYP", {})
    .then((response) => {
      dispatch(setRoutergeraettypen(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setRoutergeraettypenLoading(false)));
};

export const updateAktuellesRoutergeraet = (aktuellesRoutergeraet: routergeraet): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  if (!getState().routergeraet.editEntry) {
    dispatch(setAktuellesRoutergeraet(aktuellesRoutergeraet));
    dispatch(setViewMode());
  }
};

export const updateZS = (zs: string) => (dispatch: AppDispatch): void => {
  dispatch(setZS(zs));
};


export const createRoutergeraet = (aktuellesRoutergeraet: routergeraet): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  postJsonWithAuth("ROUTER", {}, {
    id: aktuellesRoutergeraet.id,
    seriennummer: aktuellesRoutergeraet.seriennummer,
    herstellungsjahr: aktuellesRoutergeraet.herstellungsjahr,
    firmware: aktuellesRoutergeraet.firmware,
    macadresse: aktuellesRoutergeraet.macadresse,
    kommentar: aktuellesRoutergeraet.kommentar,
    inbetriebnahmedatum: aktuellesRoutergeraet.inbetriebnahmedatum,
    wartungsintervall: aktuellesRoutergeraet.wartungsintervall,
    zeitsynchronisationsart: aktuellesRoutergeraet.zeitsynchronisationsart,
    typ: aktuellesRoutergeraet.typ,
    ipadresse: aktuellesRoutergeraet.ipadresse,
    subnetzmaske: aktuellesRoutergeraet.subnetzmaske,
    zugeordnet: aktuellesRoutergeraet.zugeordnet,
    jemalsZugeordnet: aktuellesRoutergeraet.jemalsZugeordnet
  })
    .then((response) => {
      dispatch(
        openSnackbar({
          message: `${aktuellesRoutergeraet.seriennummer} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );

      dispatch(setIdAktuellesRoutergeraet(response));
      dispatch(addAktuellesRoutergraetToRoutergeraeteListe());
      dispatch(setAktuellesRoutergeraet(routergeraetDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const updateRoutergeraet = (aktuellesRoutergeraet: routergeraet): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("ROUTERID", {}, aktuellesRoutergeraet, aktuellesRoutergeraet.id.toString())
    .then(() => {
      dispatch(
        openSnackbar({
          message: `${aktuellesRoutergeraet.seriennummer} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(setAltesRoutergeraet(aktuellesRoutergeraet));
      dispatch(updateRoutergeraeteListe(aktuellesRoutergeraet));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const deleteRoutergeraet = (aktuellesRoutergeraet: routergeraet): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("ROUTERID", {}, aktuellesRoutergeraet, aktuellesRoutergeraet.id.toString())
    .then(() => {
      dispatch(setAktuellesRoutergeraet(routergeraetDummy));
      dispatch(removeFromRoutergeraeteListe(aktuellesRoutergeraet));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(
        openSnackbar({
          message: `${aktuellesRoutergeraet.seriennummer} wurde erfolgreich gelöscht.`,
          severity: "success",
          duration: 6000,
        })
      )
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));


};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesRoutergeraet());
  dispatch(resetEditEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesRoutergeraet());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuellesRoutergeraet(routergeraetDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};



export const {
  setRoutergeraeteListe,
  setAktuellesRoutergeraet,
  removeFromRoutergeraeteListe,
  setZSs,
  setRoutergeraettypen,
  setZS,
  setRoutergeraettyp,
  resetAktuellesRoutergeraet,
  setHerstellungsjahr,
  setFirmware,
  setMACAdresse,
  setIp,
  setSubnetzmaske,
  setKommentar,
  setInbetriebnahmedatum,
  setWartungsintervall,
  setAltesRoutergeraet,
  updateRoutergeraeteListe,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  setSeriennummer,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  setError,
  resetError,
  setIdAktuellesRoutergeraet,
  addAktuellesRoutergraetToRoutergeraeteListe,
  setWartungsintervalleList,
  setListLoading,
  setRoutergeraettypenLoading,
  setZeitsynchroLoading,
  setWartungsintervallLoading,
  setSorting
} = routergeraetSlice.actions;
export default routergeraetSlice.reducer;
